import { useState } from "react";

import { CreditCard, Eye, Filter, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import moment from "moment";
import { toast } from "sonner";

import ActionMenu from "../../../components/shared/ActionMenu";
import CurrencyDisplay from "../../../components/shared/CurrencyDisplay";
import DataTable from "../../../components/shared/DataTable";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";

const PaymentsTab = ({
  payments,
  vendors,
  projects,
  fetchVendorData,
  onEditPayment,
  onViewDocument,
  filters,
  onFilterChange,
  showDeleted,
  setShowDeleted,
  setPaymentPage
}) => {
  const { user } = useAuth();
  const api = useApi();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);

  const openDeleteDialog = (payment) => {
    setDeletingPayment(payment);
    setIsDeleteOpen(true);
  };

  const deletePayment = async () => {
    try {
      const response = await api.delete(`/payments/${deletingPayment.id}`);
      if (response.status === 200 || response.status === 204) {
        toast.success("Payment deleted successfully!");
        setIsDeleteOpen(false);
        setDeletingPayment(null);
        fetchVendorData();
      } else {
        toast.error("Failed to delete payment");
      }
    } catch (error) {
      toast.error(error.message || "Error deleting payment");
    }
  };

  const paymentColumns = [
    {
      header: "Payee",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.vendorId !== "all" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Payee</p>
              <div className="space-y-2">
                <p className="text-[10px] text-slate-400 font-medium uppercase">Vendors</p>
                <LazySearchSelect
                  value={filters.vendorId}
                  onChange={(val) => onFilterChange("vendor", val)}
                  apiUrl="/vendors"
                  labelKey="vendorName"
                  placeholder="Select vendor"
                  searchPlaceholder="Search vendors..."
                  showAllOption={true}
                  allOptionLabel="All Vendors"
                  allOptionValue="all"
                />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] text-slate-400 font-medium uppercase">Contractors</p>
                <LazySearchSelect
                  value={filters.contractorId}
                  onChange={(val) => onFilterChange("contractor", val)}
                  apiUrl="/contractors"
                  labelKey="contractorName"
                  placeholder="Select contractor"
                  searchPlaceholder="Search contractors..."
                  showAllOption={true}
                  allOptionLabel="All Contractors"
                  allOptionValue="all"
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "vendor",
      isPrimary: true,
      render: (_, payment) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100">
              {payment.payeeType === "contractor" ? payment.contractor?.contractorName : payment.vendor?.vendorName || "Unknown"}
            </span>
            {payment.deleted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                Deleted
              </span>
            )}
          </div>
          <span className="text-[10px] text-slate-400 uppercase font-medium">{payment.payeeType || "Vendor"}</span>
        </div>
      )
    },
    {
      header: "Project",
      accessor: "project",
      render: (val) => <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{val?.name || "N/A"}</span>
    },
    {
      header: "Invoice",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.invoiceId !== "all" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Invoice</p>
              <LazySearchSelect
                value={filters.invoiceId}
                onChange={(val) => onFilterChange("invoice", val)}
                apiUrl="/invoices"
                labelKey="invoiceNumber"
                placeholder="Select invoice"
                searchPlaceholder="Search invoices..."
                showAllOption={true}
                allOptionLabel="All Invoices"
                allOptionValue="all"
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "invoice",
      render: (val) => (
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{val?.invoiceNumber || "N/A"}</span>
      )
    },
    {
      header: "Date",
      accessor: "paymentDate",
      sortable: true,
      sortKey: "paymentDate",
      render: (val) => moment(val).format("DD/MM/YYYY"),
      cellClassName: "text-xs text-slate-600 dark:text-slate-400"
    },
    {
      header: "Amount",
      accessor: "amount",
      render: (val) => (
        <span className="font-mono font-bold text-green-600 dark:text-green-400">
          <CurrencyDisplay amount={val} />
        </span>
      )
    },
    {
      header: "Status",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.status !== "all" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="end">
            {[
              { label: "All Status", value: "all" },
              { label: "Completed", value: "completed" },
              { label: "Pending", value: "pending" },
              { label: "Partially", value: "partially" },
              { label: "Failed", value: "failed" },
              { label: "Cancelled", value: "cancelled" }
            ].map((s) => (
              <Button
                key={s.value}
                variant={filters.status === s.value ? "secondary" : "ghost"}
                className="w-full justify-start text-sm font-normal h-9 px-3"
                onClick={() => onFilterChange("status", s.value)}
              >
                {s.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
      ),
      accessor: "paymentStatus",
      render: (val) => (
        <span
          className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider ${
            val === "completed"
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : val === "pending"
                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
          }`}
        >
          {val || "N/A"}
        </span>
      )
    },
    {
      header: "Reference",
      accessor: "referenceNumber",
      cellClassName: "text-xs font-mono text-slate-500 dark:text-slate-500"
    },
    {
      header: "Notes",
      accessor: "notes",
      render: (val) => (
        <span className="text-xs text-slate-500 dark:text-slate-500 truncate max-w-[150px] block">{val || "-"}</span>
      )
    },
    {
      header: "Actions",
      filter: user?.permissions?.payment?.delete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setPaymentPage(0);
                }}
                id="show-deleted-payments-table"
              />
              <Label htmlFor="show-deleted-payments-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, payment) => (
        <ActionMenu
          canEdit={user?.permissions?.payment?.update && !payment.deleted}
          canDelete={user?.permissions?.payment?.delete && !payment.deleted}
          onEdit={() => onEditPayment(payment)}
          onDelete={() => openDeleteDialog(payment)}
          extraActions={
            payment.documents && payment.documents.length > 0
              ? [
                  {
                    label: payment.documents.length > 1 ? "View Receipts" : "View Receipt",
                    icon: Eye,
                    onClick: () => onViewDocument(payment.documents)
                  }
                ]
              : []
          }
        />
      )
    }
  ];

  return (
    <div className="flex-1 flex flex-col space-y-4">
      <DataTable
        columns={paymentColumns}
        data={payments}
        emptyStateTitle="No payments found"
        emptyStateDescription="Record your vendor payments and transaction history."
        emptyStateIcon={CreditCard}
        className="flex-1"
        sortBy={filters.sortBy}
        direction={filters.direction}
        onSort={(key, dir) => {
          onFilterChange("sortBy", key);
          onFilterChange("direction", dir);
        }}
      />

      {/* Delete Payment Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the payment record for "
              {deletingPayment?.payeeType === "contractor"
                ? deletingPayment?.contractor?.contractorName
                : deletingPayment?.vendor?.vendorName || "Unknown"}
              ". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deletePayment} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PaymentsTab;
